import {
  Button,
  CircularProgress,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useState } from 'react';

import {
  GoogleCalendar as GraphQLGoogleCalendar,
  useGetGoogleCalendarsQuery,
  useGetLoggedInUserQuery,
} from '../../../../../graphql/gen/types';
import { spacings } from '../../../../../theme';
import { PlexFormGoogleCalendar } from './types';

type Props = {
  googleCalendars: PlexFormGoogleCalendar[];
  onAddGoogleCalendar: (calendar: PlexFormGoogleCalendar) => void;
  onRemoveGoogleCalendar: (calendars: PlexFormGoogleCalendar) => void;
};

type State = {
  query: string;
  calendar: GraphQLGoogleCalendar | null;
};

export const PlexGoogleCalendarsSection = React.memo(
  ({ googleCalendars, onAddGoogleCalendar, onRemoveGoogleCalendar }: Props): React.ReactElement => {
    const classes = useStyles();
    const [state, setState] = useState<State>({ query: '', calendar: null });

    const { data: googleCalendarData, loading: googleCalendarsLoading } = useGetGoogleCalendarsQuery();
    const { data: userData, loading: userLoading } = useGetLoggedInUserQuery();
    const loading = googleCalendarsLoading || userLoading;
    const formDisabled = !userData?.currentUser?.hasExtraGSuiteScopes;
    const existingCalendarIdSet = new Set(googleCalendars.map((c) => c.googleCalendarId));
    // Filter out existing calendars
    const allGoogleCalendars = (googleCalendarData?.currentUser?.googleCalendars || []).filter(
      (c) => !existingCalendarIdSet.has(c.id)
    );
    // Submit button should be disabled if there is no selected calendar
    const submitDisabled = formDisabled || state.calendar == null;

    const handleCalendarQueryChange = (_: React.ChangeEvent<unknown>, value: string | null): void => {
      setState({ ...state, query: value || '' });
    };

    const handleSelectCalendar = (_: React.ChangeEvent<unknown>, value: GraphQLGoogleCalendar | null): void => {
      if (value) {
        setState({ ...state, calendar: value });
      }
    };

    const handleAddCalendar = (): void => {
      if (state.calendar) {
        const { id, summary, description } = state.calendar;
        onAddGoogleCalendar({ googleCalendarId: id, summary, description });
      }
      setState({ query: '', calendar: null });
    };

    const calendarAutocomplete = (
      <Autocomplete
        clearOnEscape
        disabled={formDisabled}
        noOptionsText="No such calendar"
        onChange={handleSelectCalendar}
        value={state.calendar}
        options={allGoogleCalendars}
        onInputChange={handleCalendarQueryChange}
        inputValue={state.query}
        getOptionLabel={(option): string => `${option.summary}`}
        renderInput={(params: TextFieldProps): React.ReactElement => (
          <TextField
            {...params}
            label="Selected Google Calendar"
            variant="outlined"
            className={classes.typeTextField}
          />
        )}
      />
    );

    return (
      <div className={classes.root}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell className={classes.actionColumn} />
            </TableRow>
          </TableHead>
          <TableBody>
            {googleCalendars.map((c) => (
              <TableRow key={c.id || c.googleCalendarId}>
                <TableCell>{c.summary || ''}</TableCell>
                <TableCell>
                  <IconButton disabled={formDisabled} onClick={(): void => onRemoveGoogleCalendar(c)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className={classes.searchFormContainer}>
          {loading && (
            <div>
              <CircularProgress className={classes.progress} color="inherit" size={20} />
            </div>
          )}
          {!loading && (
            <>
              {formDisabled && (
                <Tooltip title="You must enable GSuite Integration to acccess this feature">
                  <span>{calendarAutocomplete}</span>
                </Tooltip>
              )}
              {!formDisabled && calendarAutocomplete}
              <div>
                <Button variant="contained" disabled={submitDisabled} onClick={handleAddCalendar}>
                  Add
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
);

const useStyles = makeStyles((theme) => ({
  root: {
    marginLeft: spacings.xlarge,
    marginRight: spacings.xlarge,
  },
  progress: { margin: theme.spacing(2) },
  actionColumn: { width: '12px' },
  typeTextField: { width: 300, marginRight: theme.spacing(1) },
  searchFormContainer: { display: 'flex', marginTop: theme.spacing(2), alignItems: 'center' },
  formControl: {
    marginRight: theme.spacing(1),
  },
  channelSelect: {
    minWidth: 300,
  },
}));
